import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { startRegistration } from "@simplewebauthn/browser";
import { KeyRound, Loader2 } from "lucide-react";
import { Button } from "components/ui/button";
import { RootState } from "../../../redux/store";
import {
  usePasskeyRegisterOptionsMutation,
  usePasskeyRegisterVerifyMutation,
} from "api/account/accountApiSlice";
import { getTenantName } from "util/index";
import {
  dismissPasskeyPrompt,
  isPasskeyPromptDismissed,
  isPasskeySupported,
} from "../common/passkeyPrompt";

/**
 * Offers passkey enrollment after an OTP login (#1902, plan §3).
 *
 * An offer, never a gate — OTP remains the visible path, so declining costs
 * nothing. Hidden entirely when the browser cannot do WebAuthn, and once the
 * person has said no for this account.
 */
const PasskeyEnrollPrompt: React.FC = () => {
  const { t } = useTranslation();
  const profile = useSelector((state: RootState) => state.account.profile);
  const [getOptions, { isLoading: isStarting }] =
    usePasskeyRegisterOptionsMutation();
  const [verify, { isLoading: isVerifying }] =
    usePasskeyRegisterVerifyMutation();

  const tenant = getTenantName() ?? "default";
  const [hidden, setHidden] = useState(
    () => !profile || isPasskeyPromptDismissed(tenant, profile.id)
  );
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!profile || hidden || !isPasskeySupported()) return null;

  const handleDismiss = () => {
    dismissPasskeyPrompt(tenant, profile.id);
    setHidden(true);
  };

  const handleEnroll = async () => {
    setError(null);
    try {
      const { challengeId, options } = await getOptions().unwrap();
      const response = await startRegistration({
        optionsJSON: options as never,
      });
      await verify({
        challengeId,
        response: response as unknown as Record<string, unknown>,
      }).unwrap();
      // Enrolled — remember it so the offer does not come back next login.
      dismissPasskeyPrompt(tenant, profile.id);
      setDone(true);
    } catch (err) {
      // Cancelling the OS dialog throws too; that is a decline, not a fault,
      // so it gets the same neutral retry copy rather than an alarming error.
      setError(t("account.passkey.error"));
    }
  };

  if (done) {
    return (
      <div className="rounded-md bg-primary/10 px-4 py-3 text-sm text-foreground">
        {t("account.passkey.enrolled")}
      </div>
    );
  }

  return (
    <div className="space-y-2 rounded-md bg-primary/10 px-4 py-3">
      <div className="flex items-start gap-2">
        <KeyRound className="mt-0.5 h-5 w-5 shrink-0 text-primary" />
        <div className="space-y-1">
          <p className="text-sm font-medium text-foreground">
            {t("account.passkey.prompt_title")}
          </p>
          <p className="text-sm text-slate-600">
            {t("account.passkey.prompt_description")}
          </p>
        </div>
      </div>
      {error && <p className="text-sm text-red-600">{error}</p>}
      <div className="flex gap-2">
        <Button
          size="sm"
          variant="primary"
          disabled={isStarting || isVerifying}
          onClick={handleEnroll}
        >
          {isStarting || isVerifying ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              {t("account.passkey.setting_up")}
            </>
          ) : (
            t("account.passkey.enroll")
          )}
        </Button>
        <Button size="sm" variant="outline" onClick={handleDismiss}>
          {t("account.passkey.not_now")}
        </Button>
      </div>
    </div>
  );
};

export default PasskeyEnrollPrompt;
